import { useState, useEffect } from "react";
import TableUI from "./TableUI";
import ConfigEditor from "./ConfigEditor";
import {
  FaCog,
  FaFileExport,
  FaTrash,
  FaFileExcel,
  FaFileCsv,
} from "react-icons/fa";
import DateRangePicker from "./DateRangePicker";
import { exportToExcel, exportToCSV } from "./utils";
import Toast from "./Toast";

const SmartGrid = ({
  initialConfig,
  initialData,
  selectedRows = [],
  handleRowSelection = (rows) => {},
  handleDeleteSelectedRows = (rows) => {},
  handleUpdateRow = (row, id) => {},
  handleDeleteRow = (id) => {},
  isLoading = false,
}) => {
  const [config, setConfig] = useState(initialConfig);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [showConfigEditor, setShowConfigEditor] = useState(false);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });

  const [toast, setToast] = useState({
    message: "",
    isOpen: false,
    type: "",
  });

  // Fetch data on initial load and when pagination changes
  useEffect(() => {
    if (initialData) {
      setData(initialData);
      setFilteredData(initialData);
      setTotalItems(initialData.length);
    }
  }, [initialData]);

  // Handle config update
  const handleConfigUpdate = (newConfig) => {
    setConfig(newConfig);
    setShowConfigEditor(false);
  };

  const closeToast = () => {
    setTimeout(() => {
      setToast({ message: "", isOpen: "", type: "" });
    }, [config?.toastDuration]);
  };
  // Handle export
  const handleExport = (type) => {
    if (!dateRange.startDate || !dateRange.endDate) {
      setToast({
        message: "Please select a valid date range.",
        isOpen: true,
        type: "error",
      });
      closeToast();
      setShowExportOptions(false);
      return;
    }

    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);
    const dateField = config.exportDateField || "createdAt";

    const dataToExport = filteredData.filter((item) => {
      const itemDate = new Date(item[dateField]);
      return itemDate >= startDate && itemDate <= endDate;
    });

    if (dataToExport.length === 0) {
      setToast({
        message: "No data available in selected date range.",
        isOpen: true,
        type: "error",
      });
      closeToast();
      setShowExportOptions(false);
      return;
    }

    if (type === "csv") {
      exportToCSV(dataToExport, config.columns, "table-data");
    } else if (type === "excel") {
      exportToExcel(dataToExport, config.columns, "table-data");
    }

    setShowExportOptions(false);
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="table-grid-container">
      {/* Header toolbar */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-2">
        <h2 className="text-xl font-semibold text-gray-800">
          {config.title || "Dynamic Table"}
        </h2>

        <div className="flex items-center space-x-2">
          {/* Settings button */}
          <button
            onClick={() => setShowConfigEditor(!showConfigEditor)}
            className="flex items-center px-3 py-1.5 bg-gray-200 text-gray-700 rounded-md text-sm hover:bg-gray-300"
          >
            <FaCog className="mr-1" /> Settings
          </button>

          {/* Export button */}
          <div className="relative">
            <button
              onClick={() => setShowExportOptions(!showExportOptions)}
              className="flex items-center px-3 py-1.5 bg-primary text-white rounded-md text-sm hover:bg-primary/90"
            >
              <FaFileExport className="mr-1" /> Export
            </button>

            {showExportOptions && (
              <div className="absolute left-0 md:left-auto md:right-0 mt-2 w-64 bg-white rounded-md shadow-lg z-50 p-3 border border-gray-200">
                <div className="mb-3">
                  <DateRangePicker
                    startDate={dateRange.startDate}
                    endDate={dateRange.endDate}
                    onChange={setDateRange}
                  />
                </div>
                <div className="flex flex-col space-y-2">
                  <button
                    onClick={() => handleExport("csv")}
                    className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md text-sm flex gap-1 items-center hover:bg-gray-200 text-left"
                  >
                    <FaFileCsv className=" text-blue-500" /> Export as CSV
                  </button>
                  <button
                    onClick={() => handleExport("excel")}
                    className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md text-sm flex gap-1 items-center hover:bg-gray-200 text-left"
                  >
                    <FaFileExcel className=" text-green-500" /> Export as Excel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Selected rows toolbar */}
      {selectedRows.length > 0 && (
        <div className="bg-gray-100 p-3 rounded-md mb-4 flex justify-between items-center">
          <div className="text-sm font-medium">
            {selectedRows.length} {selectedRows.length === 1 ? "row" : "rows"}{" "}
            selected
          </div>
          <button
            onClick={() => {
              handleDeleteSelectedRows(selectedRows);
            }}
            className="flex items-center px-3 py-1.5 bg-red-500 text-white rounded-md text-sm hover:bg-red-600"
          >
            <FaTrash className="mr-1" /> Delete Selected
          </button>
        </div>
      )}

      {/* Config editor */}
      {showConfigEditor && (
        <div className="mb-4">
          <ConfigEditor config={config} onSave={handleConfigUpdate} />
        </div>
      )}

      {/* Table component */}
      <TableUI
        config={config}
        data={data}
        filteredData={filteredData}
        setFilteredData={setFilteredData}
        handleDeleteRow={handleDeleteRow}
        currentPage={currentPage}
        totalItems={totalItems}
        handlePageChange={handlePageChange}
        handleRowSelection={handleRowSelection}
        selectedRows={selectedRows}
        handleUpdateRow={handleUpdateRow}
        isLoading={isLoading}
        setTotalItems={setTotalItems}
      />

      <Toast {...toast} />
    </div>
  );
};

export default SmartGrid;
